import React, { useCallback, useContext, useState } from 'react';
import classNames from 'classnames';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { getRoutePathById, allSiteMap } from '@bingads-webui-react/with-site-map';
import { Text } from '@fluentui/react-components';

import { IOverviewTableProps, IScopeContext } from './types';
import { GridDataTable } from './grid-data-table';
import { AddKeywordDrawer } from './search-term-components/add-keyword-drawer';
import { useStyles } from './overview-grid-card-style';

const getPopoverContentFunction = i18n => ({
  Name, Keyword,
}: {
  Name: string;
  Keyword: {
    Text: string;
  };
}) => (
  <>
    <Text block style={{ marginBottom: '13px' }}>{Name}</Text>
    {
      Keyword?.Text && (
        <>
          <Text weight="semibold" block>{i18n.getString(_TL_('Matched keyword'))}</Text>
          <Text block>{Keyword.Text}</Text>
        </>
      )
    }
  </>
);

export function OverviewSearchTermTable(props: IOverviewTableProps) {
  const { i18n, jsdata } = props;
  const { scope } = useContext(ScopeContext) as IScopeContext;
  const {
    aid, cid, uid, campaignId,
  } = scope?.values;
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedAdGroup, setSelectedAdGroup] = useState(null);
  const [isNegative, setIsNegative] = useState(false);
  const [keywordText, setKeywordText] = useState('');
  const classes = useStyles();

  const onFooterButtonClick = useCallback(() => {
    scope.save(getRoutePathById(allSiteMap, 'search-terms'));
  }, [scope]);

  const getHref = useCallback(() => {
    const route = getRoutePathById(allSiteMap, 'search-terms');
    const search = `cid=${cid}&uid=${uid}&aid=${aid}`;
    return `${route}?${search}`;
  }, [aid, cid, uid]);

  const triggerSearchTermDrawer = useCallback(({
    campaign, adGroup, isNegative, keywordText,
  }) => {
    setSelectedCampaign(campaign);
    setSelectedAdGroup(adGroup);
    setKeywordText(keywordText);
    setIsNegative(isNegative);
    setShowDrawer(true);
  }, []);

  const [tableKey, setTableKey] = useState(0);
  const onClose = useCallback((dataSaved: boolean = false) => {
    setShowDrawer(false);
    if (dataSaved) {
      // if data saved, we use key update to force table to fetch new data
      setTableKey(tableKey + 1);
    }
  }, [tableKey]);

  return (
    <>
      {showDrawer && (
        <AddKeywordDrawer
          campaign={selectedCampaign}
          adGroup={selectedAdGroup}
          keywordText={keywordText}
          isNegative={isNegative}
          onClose={onClose}
          i18n={i18n}
          jsdata={jsdata}
          cid={cid}
          aid={aid}
          campaignId={campaignId}
        />
      )}
      <div className={classNames(classes.gridStyle, 'ba-search-term-overview-table')}>
        <GridDataTable
          key={tableKey}
          {...props}
          entityDisPlayName={i18n.getString(_TL_('Search term'))}
          footerButtonConfigs={[
          {
            text: i18n.getString(_TL_('View all search terms')),
            onClick: onFooterButtonClick,
          },
        ]}
          getHref={getHref}
          getPopoverContent={getPopoverContentFunction(i18n)}
          triggerSearchTermDrawer={triggerSearchTermDrawer}
          entityType="SearchTerm"
        />
      </div>
    </>

  );
}
